import vueCartSvc from "../../services/vueCartSvc";
import { getObjProperty } from "../../utilities/mrVueUtils";

const state = {
  cart: {
    addons: [],
    items: []
  },
  loadingCart: false,
  cartUpdated: 0,
  addingProduct: null,
  updatingProduct: false,
  removingProduct: false,
  editAllItems: false
};

export const getters = {
  qtyByProductType: (state) => (productType) => {
    let totalQty = 0;
    let cart = state.cart;
    if (!cart.items || !cart.items.length) {
      return totalQty;
    }

    cart.items.forEach(item => {
      if (item.product_type == productType) {
        totalQty += item.qty;
      }
    });

    return totalQty;
  },

  suggestedAddons(state) {
    let addons = state.cart.addons || [];
    let items = state.cart.items || [];

    addons = addons.filter(addon => addon.pdpRecommended && !items.some(item => item.id == addon.id));

    return addons.sort((a, b) => a.xsell_rank - b.xsell_rank);
  },

  productIdInCart: (state) => (id) => {
    return state.cart.items.some(item => item.id == id);
  },

  subscribableItems(state) {
    return new Set(state.cart.items.filter((item) => item.subscription == 0 && !!item.product_type_sub_frequencies && !item.isExtraTube));
  },

  freeItemsMap(state) {
    return getObjProperty(state.cart, 'totals.freeItemsMap');
  },

  numMemberships(state) {
    let count = 0;
    (state.cart.items || []).forEach(item => {
      if (item.subscription > 0) {
        count++;
      }
    });

    return count;
  },

  // @precondition state.cartUpdated > 0
  appliedPromoByType: (state) => (type) => {
    if (!state.cartInitialized) {
      throw new Error('cart has not been loaded yet.');
    }

    let isApplied = false;
    let i = 0;
    while (i < state.cart.totals.appliedPromos.length && !isApplied) {
      for (let offer of state.cart.totals.appliedPromos[i].offers) {
        if (offer.type === type) {
          isApplied = true;
          break;
        }
      }
      i++;
    }
    return isApplied;
  },

  subWithSubAttachments(state) {
    return state.cart.items.find(item => item.subscription && item.hasOwnProperty('subscriptionAttachments')) || {};
  }
};

export const actions = {
  loadCart({ commit }) {
    commit('setLoadingCart', true);
    return new Promise((resolve) => {
      vueCartSvc.getCartDataForView({
        noCookieSave: true,
        keys: ['all']
      }).then(res => {
        let cart = res.data;
        // boost rank of Professional Color Tool Kit for pdp
        (cart.addons || []).forEach(addon => {
          if (addon.id == 145) {
            addon.xsell_rank = 100;
          }
        });

        commit('setCart', cart);
        resolve(cart);
      }).finally(() => {
        commit('setLoadingCart', false);
      });
    });
  },

  addToCart({ commit, dispatch, state }, params) {
    if (state.addingProduct) {
      return;
    }

    return new Promise((resolve, reject) => {
      commit('setAddingProduct', params.productId);

      var payload = {
        product_id: params.productId,
        qty: params.quantity || 1,
        subscription: params.subscription || 0
      };

      if (params.giftCard) {
        payload.giftCard = params.giftCard;
      }

      //- DO NOT use this property, left here for any loose ends. For actions on refillCart use refillCart store
      if (params.cartType) {
        payload.cartType = params.cartType;
      }

      if (params.ctaSource) {
        payload.ctaSource = params.ctaSource;
      }

      if (params.validOffer) {
        payload.validOffer = params.validOffer;
      }

      if (params.membershipAddon) {
        payload.membershipAddon = params.membershipAddon;
      }

      if (params.attachableProdIds) {
        payload.attachableProdIds = params.attachableProdIds;
      }

      if (params.extra) {
        payload.addExtraTube = params.extra;
      }

      vueCartSvc.addToCart(payload).then(() => {
        dispatch('loadCart');
        commit('setCartUpdated');
        resolve();
      }).catch(() => {
        reject();
      }).finally(() => commit('setAddingProduct', null));
    });
  },

  updateItemQty({ commit, dispatch, state }, params) {
    if (state.updatingProduct) {
      return;
    }
    if (params.qty < 1 || params.qty > 10) {
      return;
    }
    commit('setUpdatingProduct', true);

    var payload = {
      index: params.index,
      qty: params.qty,
      ctaSource: params.ctaSource
    };

    vueCartSvc.updateItemQty(payload).then(() => {
      dispatch('loadCart');
      commit('setCartUpdated');
      commit('setUpdatingProduct', false);
    }).catch(() => {
      commit('setUpdatingProduct', false);
      dispatch('notifyError', null, { root: true });
    });
  },

  updateItemSubscription({ commit, dispatch, state }, params) {
    if (state.updatingProduct) {
      return;
    }
    commit('setUpdatingProduct', true);

    return new Promise((resolve, reject) => {
      var payload = {
        index: params.index,
        subscription: params.subscription,
        ctaSource: params.ctaSource
      };

      //- DO NOT use this property, left here for any loose ends. For actions on refillCart use refillCart store
      if (params.cartType) {
        payload.cartType = params.cartType;
      }

      vueCartSvc.updateItemSubscription(payload).then(() => {
        commit('setUpdatingProduct', false);
        dispatch('loadCart');
        resolve();
      }).catch(() => {
        commit('setUpdatingProduct', false);
        dispatch('notifyError', null, { root: true });
        reject();
      });
    });
  },

  removeFromCart({ commit, dispatch, state }, params) {
    if (state.removingProduct) {
      return;
    }

    commit('setRemovingProduct', true);

    var payload = {
      index: params.index,
      ctaSource: params.ctaSource
    };

    //- DO NOT use this property, left here for any loose ends. For actions on refillCart use refillCart store
    if (params.cartType) {
      payload.cartType = params.cartType;
    }

    vueCartSvc.removeFromCart(payload)
      .then(() => {
        commit('setRemovingProduct', false);
        dispatch('loadCart');
        commit('setCartUpdated');
      })
      .catch(() => {
        commit('setRemovingProduct', false);
        dispatch('notifyError', null, { root: true });
      });
  },

  applyCoupon({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      vueCartSvc.applyCoupon(params).then(() => {
        dispatch('loadCart');
        commit('setCartUpdated');
        resolve();
      }).catch(err => {
        reject(err.response.data);
      });
    });
  },

  openRemovePromoModal({ dispatch }, params) {
    dispatch('modal/showModal', {
      component: './Cart/CartTotals/CartRemovePromoModal',
      props: {
        promoId: params.promoId
      }
    }, { root: true });
  },

  removeCoupon({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      vueCartSvc.removeCoupon(params).then(() => {
        dispatch('loadCart');
        commit('setCartUpdated');
        resolve();
      }).catch(err => {
        reject(err.response.data);
      });
    });
  },

  moveToOtherCart({ dispatch }, params) {
    vueCartSvc.moveToOtherCart(params).then(() => {
      //- refresh both carts
      dispatch('loadCart');
      dispatch('refillCart/loadRefillCart', null, { root: true });
    });
  }

};

export const mutations = {
  setCart(state, val) {
    state.cart = val;
    if (!state.cartInitialized) {
      state.cartInitialized = true;
    }
  },

  setLoadingCart(state, val) {
    state.loadingCart = val;
  },

  setCartUpdated(state) {
    state.cartUpdated += 1;
  },

  setAddingProduct(state, val) {
    state.addingProduct = val;
  },

  setUpdatingProduct(state, val) {
    state.updatingProduct = val;
  },

  setRemovingProduct(state, val) {
    state.removingProduct = val;
  },

  setEditAllItems(state, val) {
    state.editAllItems = Boolean(val);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
